import React from 'react';
import { motion } from 'framer-motion';
import { useGameStore } from '../store/gameStore';

const ProfessionalTimeline: React.FC = () => {
  const { currentAge, timeline } = useGameStore();

  // Calculate timeline progress (0-100%)
  const timelineProgress = (currentAge / 100) * 100;

  // Get major life stages
  const lifeStages = [
    { age: 0, label: 'Birth', color: '#fbbf24' },
    { age: 18, label: 'Adult', color: '#3b82f6' },
    { age: 25, label: 'Young Adult', color: '#8b5cf6' },
    { age: 35, label: 'Middle Age', color: '#10b981' },
    { age: 50, label: 'Mature', color: '#f59e0b' },
    { age: 65, label: 'Senior', color: '#ef4444' },
    { age: 100, label: 'Century', color: '#6b7280' },
  ];


  return (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-white">Life Timeline</h3>
          <p className="text-slate-400 text-sm">Your journey through life</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-emerald-400">{currentAge}</div>
          <div className="text-slate-400 text-sm">years old</div>
        </div>
      </div>

      {/* Timeline Line */}
      <div className="relative mb-8">
        {/* Background Line */}
        <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
          {/* Progress Line */}
          <motion.div
            className="h-full bg-gradient-to-r from-emerald-400 to-blue-400 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${timelineProgress}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
        </div>

        {/* Age Markers */}
        <div className="absolute top-0 left-0 w-full h-2 flex justify-between">
          {lifeStages.map((stage, index) => (
            <div key={stage.age} className="relative">
              {/* Marker Dot */}
              <div
                className={`absolute -top-1 w-4 h-4 rounded-full border-2 ${
                  currentAge >= stage.age
                    ? 'bg-emerald-400 border-emerald-400'
                    : 'bg-slate-700 border-slate-600'
                }`}
                style={{ left: `${(stage.age / 100) * 100}%`, transform: 'translateX(-50%)' }}
              />
              
              {/* Age Label */}
              <div
                className="absolute top-6 text-xs font-medium text-slate-400 whitespace-nowrap"
                style={{ left: `${(stage.age / 100) * 100}%`, transform: 'translateX(-50%)' }}
              >
                {stage.age}
              </div>
              
              {/* Stage Label */}
              <div
                className="absolute top-10 text-xs text-slate-500 whitespace-nowrap"
                style={{ left: `${(stage.age / 100) * 100}%`, transform: 'translateX(-50%)' }}
              >
                {stage.label}
              </div>
            </div>
          ))}
        </div>

        {/* Current Age Indicator */}
        <motion.div
          className="absolute -top-2 w-6 h-6 bg-emerald-400 border-4 border-slate-800 rounded-full shadow-lg"
          style={{ left: `${timelineProgress}%`, transform: 'translateX(-50%)' }}
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </div>


      {/* Timeline Stats */}
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-emerald-400">{timeline.length}</div>
          <div className="text-xs text-slate-400">Life Events</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-400">{100 - currentAge}</div>
          <div className="text-xs text-slate-400">Years Left</div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalTimeline;
